import React from 'react';

export interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason: 'daily_limit' | 'time_limit' | 'premium_feature';
  sessionsUsedToday?: number;
  dailyLimit?: number;
}

export const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  isOpen,
  onClose,
  reason,
  sessionsUsedToday,
  dailyLimit,
}) => {
  if (!isOpen) return null;

  const getContent = () => {
    switch (reason) {
      case 'daily_limit':
        return {
          icon: '⏰',
          title: 'Daily Limit Reached',
          message: `You've used all ${dailyLimit || 3} of your free sessions today. Upgrade to Premium for unlimited daily sessions!`,
          features: [
            '✨ Unlimited daily sessions',
            '⏱️ No 10-minute time limit',
            '⏭️ Skip to next match instantly',
            '🌍 Match with anyone worldwide',
            '🚀 Priority matching',
          ],
        };
      case 'time_limit':
        return {
          icon: '⏱️',
          title: 'Time Limit Reached',
          message: 'Your free session has reached the 10-minute limit. Upgrade to Premium for unlimited session time!',
          features: [
            '⏱️ No time limits on sessions',
            '✨ Unlimited daily sessions',
            '⏭️ Skip to next match instantly',
            '🌍 Match with anyone worldwide',
            '🚀 Priority matching',
          ],
        };
      case 'premium_feature':
        return {
          icon: '⭐',
          title: 'Premium Feature',
          message: 'This feature is available to Premium users only. Upgrade to unlock all features!',
          features: [
            '⏭️ Skip to next match',
            '✨ Unlimited daily sessions',
            '⏱️ No time limits',
            '🌍 Match with anyone worldwide',
            '🚀 Priority matching',
          ],
        };
    }
  };

  const content = getContent();

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Icon */}
        <div style={styles.iconContainer}>
          <span style={styles.icon}>{content.icon}</span>
        </div>

        {/* Title */}
        <h2 style={styles.title}>{content.title}</h2>

        {/* Message */}
        <p style={styles.message}>{content.message}</p>

        {/* Features List */}
        <div style={styles.featuresSection}>
          <h3 style={styles.featuresTitle}>Premium Benefits:</h3>
          <ul style={styles.featuresList}>
            {content.features.map((feature, index) => (
              <li key={index} style={styles.featureItem}>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Pricing */}
        <div style={styles.pricingSection}>
          <div style={styles.priceTag}>
            <span style={styles.priceAmount}>$9.99</span>
            <span style={styles.pricePeriod}>/month</span>
          </div>
          <p style={styles.priceDescription}>Cancel anytime, no commitment</p>
        </div>

        {/* Action Buttons */}
        <div style={styles.actions}>
          <button onClick={onClose} style={styles.closeButton}>
            Maybe Later
          </button>
          <button
            onClick={() => {
              // TODO: Implement upgrade flow
              window.location.href = '/premium';
            }}
            style={styles.upgradeButton}
          >
            ⭐ Upgrade to Premium
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '2.5rem',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
    textAlign: 'center',
  },
  iconContainer: {
    marginBottom: '1.5rem',
  },
  icon: {
    fontSize: '4rem',
    display: 'inline-block',
    animation: 'bounce 2s ease infinite',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    marginBottom: '1rem',
    margin: 0,
  },
  message: {
    fontSize: '1.1rem',
    color: 'var(--color-gray-700)',
    marginBottom: '2rem',
    lineHeight: '1.6',
  },
  featuresSection: {
    marginBottom: '2rem',
    textAlign: 'left',
  },
  featuresTitle: {
    fontSize: '1.2rem',
    fontWeight: '600',
    color: 'var(--color-deep-purple)',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  featuresList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  featureItem: {
    fontSize: '1rem',
    color: 'var(--color-gray-700)',
    padding: '0.75rem 1rem',
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: 'var(--radius-md)',
    marginBottom: '0.5rem',
    fontWeight: '500',
  },
  pricingSection: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: 'rgba(251, 191, 36, 0.1)',
    borderRadius: 'var(--radius-md)',
    border: '2px solid #fbbf24',
  },
  priceTag: {
    marginBottom: '0.5rem',
  },
  priceAmount: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: 'var(--color-warm-orange)',
  },
  pricePeriod: {
    fontSize: '1.2rem',
    color: 'var(--color-gray-600)',
    marginLeft: '0.5rem',
  },
  priceDescription: {
    fontSize: '0.9rem',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  closeButton: {
    padding: '0.875rem 2rem',
    backgroundColor: 'transparent',
    color: 'var(--color-gray-600)',
    border: '2px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  upgradeButton: {
    padding: '0.875rem 2rem',
    background: 'linear-gradient(135deg, var(--color-warm-orange), #f59e0b)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1.1rem',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(234, 88, 12, 0.3)',
  },
};
